import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { initAppInsights } from './lib/app-insights';
import { setTokenProvider } from './api/client';
import { Auth0ProviderWithRouter } from './features/auth/auth0-provider-with-router';
import { initSyncController, disposeSyncController } from './lib/offline/sync-controller';
import { runEagerPrefetch } from './lib/offline/eager-prefetch';
import { ThemeProvider, applyInitialThemeFromLocalStorage } from './features/theme/theme-provider';
import './i18n';
import './styles/globals.css';

// Synchroniczny apply z localStorage przed React mount — uniknięcie FOUC dla returning user.
applyInitialThemeFromLocalStorage();

import { routeTree } from './routeTree.gen';

initAppInsights();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: {
    queryClient,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Wires Auth0 `getAccessTokenSilently` do api/client.ts `setTokenProvider`.
 * Mounted inside Auth0Provider — useAuth0 hook accessible.
 * Must be a separate component because hooks need React tree.
 */
function TokenProviderWiring({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();

  // setTokenProvider SYNCHRONICZNIE w body (NIE useEffect) — useEffect runs PO render,
  // PO odpaleniu queries enabled=isAuthenticated. Race: useBootstrap fires → buildHeaders
  // → tokenProvider null → request bez Bearer → 403 "Missing or malformed Authorization
  // header". React Query retry (retry:1) załatwia ale 403 w logach przy każdym wejściu.
  // Side-effect w body OK bo setTokenProvider jest idempotent (replace closure każdy render).
  setTokenProvider(async () => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently();
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    initSyncController({
      getAccessTokenSilently: () => getAccessTokenSilently(),
      onAuthFailure: () => {
        void loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search },
        });
      },
      onDeletedCards: (cardIds) => {
        window.dispatchEvent(
          new CustomEvent('offline-deleted-cards', { detail: { count: cardIds.length } }),
        );
      },
    });
    void runEagerPrefetch();
    return () => {
      disposeSyncController();
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  return <>{children}</>;
}

/**
 * PWA update notification per spec §6.3 — wykrywa nową wersję SW (post-deploy),
 * pokazuje toast z manualnym "Odśwież" buttonem (NIE auto-reload — nie ucinamy sesji nauki).
 */
function PwaUpdateToast() {
  const { t } = useTranslation();
  const {
    needRefresh: [show],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      console.warn('[pwa-register] error', error);
    },
  });
  if (!show) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 transform border border-fuchsia-500/40 bg-[var(--color-bg-primary)] px-6 py-4 text-sm font-black uppercase tracking-[0.3em] text-[var(--color-text-primary)] shadow-2xl"
    >
      <span className="mr-4">{t('offline.appUpdateAvailable')}</span>
      <button
        type="button"
        onClick={() => updateServiceWorker(true)}
        className="border border-fuchsia-500 bg-fuchsia-500/10 px-3 py-1 text-fuchsia-300 transition-colors hover:bg-fuchsia-500 hover:text-white"
      >
        {t('offline.appUpdateRefresh')}
      </button>
    </div>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Auth0ProviderWithRouter>
        <TokenProviderWiring>
          <ThemeProvider>
            <RouterProvider router={router} />
            <PwaUpdateToast />
          </ThemeProvider>
        </TokenProviderWiring>
      </Auth0ProviderWithRouter>
    </QueryClientProvider>
  </StrictMode>,
);
